import type { Row } from "@rocicorp/zero";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import type { DisplayableSpace } from "../hooks/useSpaceDisplayName";
import { spaceIcon } from "../lib/spaceIcon";
import { navigate } from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import {
  type TriggerType,
  triggerTypeIcons,
  triggerTypeLabels,
} from "../lib/workflowTrigger";
import { Avatar } from "./Avatar";
import {
  StatusFilterMenuLeft,
  StatusFilterPillLeft,
  type StatusFilterValue,
  statusFilterLabels,
} from "./BuildStatus";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { Icon } from "./Icon";
import { type FilterDef, toggleMultiFilter } from "./ListFilter";
import type { SortDirection } from "./ListTable";

export const workflowListSortColumns = {
  name: "asc",
  buildStatus: "desc",
  space: "asc",
  createdAt: "desc",
  createdBy: "asc",
} satisfies Record<string, SortDirection>;

type WorkflowFilters = {
  status?: { options: StatusFilterValue[]; value: StatusFilterValue | null };
  trigger?: { options: TriggerType[]; values: string[] };
  space?: {
    options: DisplayableSpace[];
    values: string[];
    displayName: (space: DisplayableSpace) => string;
    // A single bucket option collapsing every space the viewer isn't a member
    // of, so their names aren't revealed individually (the Links gallery).
    otherBucket?: { id: string; label: string };
  };
  app: { options: ConnectorWithOAuth[]; values: string[] };
  creator: { options: Row["user"][]; values: string[] };
};

// The filter bar shared by the workflow list views (Recents, Favorites,
// Branches, Space) and the Links gallery. Status, trigger, and space are
// optional: Branches has no trigger filter, the Space view has no space filter,
// and the Links gallery filters only by space, connector, and creator.
export function workflowListFilters({
  status,
  trigger,
  space,
  app,
  creator,
}: WorkflowFilters): FilterDef[] {
  const filters: FilterDef[] = [];

  if (status) {
    filters.push({
      kind: "single",
      key: "status",
      label: "3B status",
      icon: <Icon name="circle-dashed" size={16} />,
      options: status.options.map((option) => ({
        id: option,
        label: statusFilterLabels[option],
        menuLeft: <StatusFilterMenuLeft status={option} />,
        pillLeft: <StatusFilterPillLeft status={option} />,
      })),
      value: status.value,
      onSelect: (id) => navigate(null, { status: id }, { replace: true }),
      onClear: () => navigate(null, { status: "all" }, { replace: true }),
    });
  }

  if (trigger) {
    filters.push({
      kind: "multi",
      key: "trigger",
      label: "Trigger",
      icon: <Icon name="trigger" size={16} />,
      options: trigger.options.map((type) => ({
        id: type,
        label: triggerTypeLabels[type],
        menuLeft: <Icon name={triggerTypeIcons[type]} size={16} />,
        pillLeft: <Icon name={triggerTypeIcons[type]} size={12} />,
      })),
      values: trigger.values,
      onToggle: (id) =>
        navigate(
          null,
          { trigger: toggleMultiFilter(trigger.values, id) },
          { replace: true }
        ),
    });
  }

  if (space) {
    filters.push({
      kind: "multi",
      key: "space",
      label: "Space",
      icon: <Icon name="hash" size={16} />,
      options: [
        ...space.options.map((s) => ({
          id: s.id,
          label: space.displayName(s),
          menuLeft: <Icon name={spaceIcon(s.mode)} size={16} />,
          pillLeft: <Icon name={spaceIcon(s.mode)} size={12} />,
        })),
        ...(space.otherBucket
          ? [
              {
                id: space.otherBucket.id,
                label: space.otherBucket.label,
                menuLeft: <Icon name="eye-closed" size={16} />,
                pillLeft: <Icon name="eye-closed" size={12} />,
              },
            ]
          : []),
      ],
      values: space.values,
      onToggle: (id) =>
        navigate(
          null,
          { space: toggleMultiFilter(space.values, id) },
          { replace: true }
        ),
    });
  }

  filters.push(
    {
      kind: "multi",
      key: "app",
      label: "Connector",
      icon: <Icon name="api-key-custom" size={16} />,
      options: app.options.map((a) => ({
        id: a.id,
        label: a.name,
        menuLeft: (
          <ConnectorLogoContainer size={16}>
            <ConnectorLogo app={a} size={10} />
          </ConnectorLogoContainer>
        ),
        pillLeft: (
          <ConnectorLogoContainer size={12}>
            <ConnectorLogo app={a} size={8} />
          </ConnectorLogoContainer>
        ),
      })),
      values: app.values,
      onToggle: (id) =>
        navigate(
          null,
          { app: toggleMultiFilter(app.values, id) },
          { replace: true }
        ),
    },
    {
      kind: "multi",
      key: "creator",
      label: "Created by",
      icon: <Icon name="circle-user-round" size={16} />,
      options: creator.options.map((c) => ({
        id: c.id,
        label: userDisplayName(c),
        menuLeft: <Avatar user={c} size={16} />,
        pillLeft: <Avatar user={c} size={12} />,
      })),
      values: creator.values,
      onToggle: (id) =>
        navigate(
          null,
          { creator: toggleMultiFilter(creator.values, id) },
          { replace: true }
        ),
    }
  );

  return filters;
}
